import { useEffect, useState } from "react";

const COUNT_UP_DURATION_MS = 1150;

/**
 * Eased 0 to 1 ramp that drives a group of animated figures from a single frame
 * loop, so every figure sharing the ramp stays in step.
 *
 * `resetKey` should carry the underlying figures rather than a render count: the
 * ramp then replays when a filter changes the numbers, not on every re-render.
 */
function useCountUpProgress(resetKey: string, animate: boolean): number {
  const [progress, setProgress] = useState(animate ? 0 : 1);

  useEffect(() => {
    if (!animate) {
      setProgress(1);
      return;
    }

    let frame = 0;
    let startedAt = 0;

    const step = (now: number) => {
      if (startedAt === 0) {
        startedAt = now;
      }

      const elapsed = Math.min(1, (now - startedAt) / COUNT_UP_DURATION_MS);
      setProgress(1 - Math.pow(1 - elapsed, 3));

      if (elapsed < 1) {
        frame = window.requestAnimationFrame(step);
      }
    };

    setProgress(0);
    frame = window.requestAnimationFrame(step);

    return () => window.cancelAnimationFrame(frame);
  }, [resetKey, animate]);

  return progress;
}

export default useCountUpProgress;
